import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";

// Template176: Barrios 2 - bloques separados (tipBox, mapa, mission, doloresPark, castro, twinPeaks)
const Template176 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const [loaded, setLoaded] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(
		typeof navigator !== "undefined" ? navigator.onLine : true,
	);
	const { setMapFullscreen } = useMapFullscreen() || {};

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";

	const headerText = page.texts?.[0]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const mapUrl = page.maps?.[0] ?? "";
	const offlineMapSrc = getImageSrc("mission-district");
	const tipBox =
		page?.tipBox ?? page?.data?.tipBox ?? page?.data?.data?.tipBox ?? {};
	const tipBoxText = tipBox.text ?? "";
	const tipBoxImageSrc = tipBox.imageId ? getImageSrc(tipBox.imageId) : "";
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const mission = sections[0] ?? {};
	const doloresPark = sections[1] ?? {};
	const castro = sections[2] ?? {};
	const twinPeaks = sections[3] ?? {};
	const actualHeaderColor = headerBackgroundColor || "#F2B705";

	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* ——— Tip box sobre mapa + mapa + botón ——— */}
			<Box data-block-id="map" sx={{ marginTop: "-70px", position: "relative" }}>
				{tipBoxText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 70,
							right: 5,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: "'Poppins', sans-serif",
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 140,
							zIndex: 3,
						}}
					>
						{tipBoxText}
					</Box>
				)}
				{tipBoxImageSrc && (
					<Box
						component="img"
						src={tipBoxImageSrc}
						alt=""
						sx={{
							width: 110,
							position: "absolute",
							bottom: 0,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}

				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa Barrios SF"
						src={mapUrl}
						width="100%"
						height={isSmallScreen ? "400px" : "400px"}
						allowFullScreen
						loading="lazy"
						onLoad={() => setLoaded(true)}
						sx={{ border: 0 }}
					/>
				) : offlineMapSrc ? (
					<Box
						component="img"
						src={offlineMapSrc}
						alt=""
						sx={{
							width: "100%",
							height: isSmallScreen ? "400px" : "400px",
							objectFit: "cover",
							position: "relative",
							zIndex: 2,
						}}
					/>
				) : !isOnline ? (
					<SinConexionKawaii
						height="100%"
						fontFamily={page?.fontFamily || "inherit"}
					/>
				) : null}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}
			</Box>

			<Box
				data-block-id="text-1"
				sx={{
					display: "flex",
					justifyContent: "space-between",
					flexDirection: "column",
					height: "100%",
					flex: 1,
				}}
			>
				<Box sx={{ padding: "10px 12px", flex: 1 }}>
					<Box
						sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}
					>
						{/* ——— Mission District ——— */}
						<Box data-block-id="text-2" sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
							{getImageSrc(mission.imageId) && (
								<Box
									component="img"
									src={getImageSrc(mission.imageId)}
									alt=""
									sx={{
										width: "40%",
										borderRadius: "12px",
										objectFit: "cover",
										maxHeight: 110,
									}}
								/>
							)}
							<Box sx={{ width: "60%", position: "relative" }}>
								{mission.title && (
									<Box
										component="h3"
										sx={{
											fontFamily: "'Poppins', sans-serif",
											marginBottom: 0.5,
											fontSize: 14,
											fontWeight: 700,
											textTransform: "uppercase",
											marginTop: 1,
										}}
									>
										{mission.title}
									</Box>
								)}
								{mission.sabiasQue?.[0] && (
									<Box sx={{ position: "absolute", top: -35, right: "65%" }}>
										<SabiasQue
											icono={mission.sabiasQue[0].icono}
											texto={mission.sabiasQue[0].texto}
										/>
									</Box>
								)}
								{mission.body && (
									<Box
										component="p"
										sx={{
											fontSize: 13,
											color: contentColor,
											fontFamily: "'Poppins', sans-serif",
											margin: 0,
										}}
									>
										{mission.body}
									</Box>
								)}
								{getImageSrc(mission.stickerImageId) && (
									<Box
										component="img"
										src={getImageSrc(mission.stickerImageId)}
										alt=""
										sx={{
											width: 100,
											position: "absolute",
											bottom: -100,
											right: 0,
										}}
									/>
								)}
								{mission.tooltipText && (
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? -80 : -60,
											right: isSmallScreen ? 90 : 100,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 0px 16px",
											padding: "10px 12px",
											fontSize: 11,
											fontFamily: "'Poppins', sans-serif",
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											width: isSmallScreen ? 240 : 270,
										}}
									>
										{mission.tooltipText}
									</Box>
								)}
							</Box>
						</Box>

						{/* ——— Dolores Park ——— */}
						<Box data-block-id="text-3">
							{doloresPark.title && (
								<Box
									component="h3"
									sx={{
										fontFamily: "'Poppins', sans-serif",
										marginBottom: 0,
										fontSize: 14,
										fontWeight: 700,
										textTransform: "uppercase",
										marginTop: isSmallScreen ? 12 : 2,
									}}
								>
									{doloresPark.title}
								</Box>
							)}
							{doloresPark.body && (
								<Box
									component="p"
									sx={{
										fontSize: 13,
										fontFamily: "'Poppins', sans-serif",
										marginTop: 0,
									}}
								>
									{doloresPark.body}
								</Box>
							)}
						</Box>

						{/* ——— Castro ——— */}
						<Box data-block-id="text-4" sx={{ display: "flex", position: "relative" }}>
							<Box sx={{ width: "100%" }}>
								<Box
									sx={{
										...estiloP,
										padding: "16px",
										backgroundColor: "#fbf0cf",
										borderRadius: "12px",
									}}
								>
									{castro.title && (
										<Box component="h3" sx={estiloH3}>
											{castro.title}
										</Box>
									)}
									{castro.body && (
										<Box component="span" sx={{ display: "block" }}>
											{castro.body}
										</Box>
									)}
								</Box>
							</Box>
							{castro.sabiasQue?.[0] && (
								<Box sx={{ position: "absolute", bottom: -20, right: "50%" }}>
									<SabiasQue
										icono={castro.sabiasQue[0].icono}
										texto={castro.sabiasQue[0].texto}
									/>
								</Box>
							)}
						</Box>

						{/* ——— Twin Peaks ——— */}
						<Box
							data-block-id="text-5"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: 2,
								position: "relative",
								marginTop: isSmallScreen ? 0 : 0,
							}}
						>
							<Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
								<Box sx={{ width: "100%", position: "relative" }}>
									{twinPeaks.title && (
										<Box
											component="h3"
											sx={{
												fontFamily: "'Poppins', sans-serif",
												marginBottom: 0.5,
												fontSize: 14,
												fontWeight: 700,
												textTransform: "uppercase",
												marginTop: 1,
											}}
										>
											{twinPeaks.title}
										</Box>
									)}
									{twinPeaks.body && (
										<Box
											component="p"
											sx={{
												fontSize: 13,
												color: contentColor,
												fontFamily: "'Poppins', sans-serif",
												margin: 0,
											}}
										>
											{twinPeaks.body}
										</Box>
									)}
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template176.thumbnail = "/images/template-1.png";
export default Template176;
